import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AppState } from '../../../providers/AppStateProvider/types';
import type { PreTradeScenarioNewOrderSingle } from './preTradeApiTypes';

export interface PreTradeState {
  activeSessionId: string | null;
  /** RequestID of the scenario to potentially include on new orders. */
  preTradeRequestId: string | null;
  /** SessionID that was active when the reference was captured. Checked at read-time in OrderService. */
  referenceSessionId: string | null;
  /** Scenario NOS used to detect pre-trade-relevant form changes at submission time. */
  referenceNOS: PreTradeScenarioNewOrderSingle | null;
  /**
   * True when the user explicitly clicked "Use Scenario" to prime the form.
   * Prevents automatic API-driven reference updates from overwriting the user's selection
   * until a new scenario evaluation is requested (clearUserSelectedReference).
   */
  referenceIsUserSelected: boolean;
  /** When true, OrderService skips attaching PreTradeSessionID/PreTradeRequestID to the next new order. */
  suspendTracking: boolean;
}

export function getPreTradeInitialState(): PreTradeState {
  return {
    activeSessionId: null,
    preTradeRequestId: null,
    referenceSessionId: null,
    referenceNOS: null,
    referenceIsUserSelected: false,
    suspendTracking: false,
  };
}

const preTradeSlice = createSlice({
  name: 'preTrade',
  initialState: getPreTradeInitialState(),
  reducers: {
    setActiveSession: (state, action: PayloadAction<{ sessionId: string }>) => {
      state.activeSessionId = action.payload.sessionId;
      state.suspendTracking = false;
    },

    suspendTracking: state => {
      state.suspendTracking = true;
    },

    resumeTracking: state => {
      state.suspendTracking = false;
    },

    clearActiveSession: state => {
      state.activeSessionId = null;
      state.preTradeRequestId = null;
      state.referenceSessionId = null;
      state.referenceNOS = null;
      state.referenceIsUserSelected = false;
    },

    clearActiveSessionIfMatches: (state, action: PayloadAction<{ sessionId: string }>) => {
      if (state.activeSessionId === action.payload.sessionId) {
        state.activeSessionId = null;
        state.preTradeRequestId = null;
        state.referenceSessionId = null;
        state.referenceNOS = null;
        state.referenceIsUserSelected = false;
      }
    },

    setPreTradeReference: (
      state,
      action: PayloadAction<{
        sessionId: string;
        requestId: string;
        nos: PreTradeScenarioNewOrderSingle;
        userSelected?: boolean;
      }>
    ) => {
      // A user-selected reference (from "Use Scenario") takes priority over automatic
      // API-driven updates. If the user has already selected a scenario, ignore subsequent
      // API responses until clearUserSelectedReference is dispatched.
      if (!action.payload.userSelected && state.referenceIsUserSelected) {
        return;
      }
      state.preTradeRequestId = action.payload.requestId;
      state.referenceSessionId = action.payload.sessionId;
      state.referenceNOS = action.payload.nos;
      state.referenceIsUserSelected = action.payload.userSelected ?? false;
    },

    /** Clears the stored pre-trade reference so a stale RequestID is never attached to a new order. */
    clearPreTradeReference: state => {
      state.preTradeRequestId = null;
      state.referenceSessionId = null;
      state.referenceNOS = null;
      state.referenceIsUserSelected = false;
    },

    /** Clears the user-selected lock so the next API response can update the reference. */
    clearUserSelectedReference: state => {
      state.referenceIsUserSelected = false;
    },
  },
});

export const {
  clearActiveSession,
  clearActiveSessionIfMatches,
  clearPreTradeReference,
  clearUserSelectedReference,
  resumeTracking,
  setActiveSession,
  setPreTradeReference,
  suspendTracking,
} = preTradeSlice.actions;

export const selectActiveSessionId = (state: AppState): string | null => state.preTrade?.activeSessionId ?? null;

export const preTradeSliceReducer = preTradeSlice.reducer;
